import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  Plus, Archive, ChevronRight, Building, Landmark, Home, MapPin, AlertTriangle, Info,
} from "lucide-react";
import { motion } from "framer-motion";
import AddAssetDialog from "../components/AddAssetDialog";
import AssetDetailModal from "../components/AssetDetailModal";
import ArchiveModal from "../components/ArchiveModal";

const locationLabels = { dubai: "Dubai, UAE", north_cyprus: "North Cyprus", germany: "Germany" };
const locationIcons = { dubai: Building, north_cyprus: Home, germany: Landmark };

// Muted gold + neutral palette, cycled for ANY number of countries (no hardcoded set).
const ALLOC_COLORS = ["#D4AF37", "#B5952F", "#C9B37E", "#8C7A4A", "#9A8C66", "#6E6652", "#CBB98E", "#534F45"];
const humanize = (s) =>
  locationLabels[s] || (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unknown");

const NA = "Not available";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Investor ownership fraction (0–1). Missing / invalid → 1 (100%, whole
// property). Portfolio-level KPIs (Paid-In, Outstanding, Allocation, Est.
// Market Value) scale each asset's WHOLE-property figure by this so totals
// reflect only the investor's share. Per-property rows stay UNSCALED (they
// show the full property values). Payment/cost fields store whole-property
// amounts, so this never double-scales a personal contribution.
const ownFracOf = (a) => {
  const p = num(a.ownership_pct);
  return p > 0 && p <= 100 ? p / 100 : 1;
};

// Explicit valuation only — purchase price is NEVER used as a market-value proxy.
const valuationOf = (a) => {
  for (const k of ["estimated_market_value", "market_value", "current_market_value"]) {
    const v = a[k];
    if (v !== undefined && v !== null && v !== "" && num(v) > 0) return num(v);
  }
  return null;
};

// Derived status from existing fields (no invented data).
const statusOf = (a) => {
  const isGlobal = a.registration_mode === "simplified_global" || a.location === "other";
  let base;
  if (isGlobal && a.property_status) {
    base = a.property_status === "off_plan" ? "Off-Plan"
      : a.property_status === "under_construction" ? "Under Construction"
      : "Completed";
  } else {
    base = a.property_type === "off_plan" || a.is_new_build ? "Off-Plan" : "Completed";
  }
  return a.is_financed && num(a.loan_amount) > 0 ? `${base} · Financed` : base;
};

const PropStat = ({ label, value, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="text-[9px] tracking-[0.14em] uppercase text-white/35 font-sans">{label}</p>
    <p className={`font-mono text-[13px] mt-1 truncate ${value === NA ? "text-white/30" : "text-white/85"}`}>{value}</p>
  </div>
);

/**
 * Per-asset Paid-In Capital & Outstanding Commitments, in the asset's LOCAL
 * currency, with explicit reliability flags. No calculation engine is called
 * and no asset data is mutated — this reads only existing records.
 *
 * Definitions (documented, conservative):
 *  - `acq` = total committed capital = total_investment (purchase price + paid
 *    acquisition costs computed by the existing engine), fallback purchase_price.
 *  - A payment schedule (payment_milestones) is "usable" only when EVERY entry
 *    has an explicit paid flag and a derivable amount (amount, or percentage ×
 *    purchase_price). A present-but-ambiguous schedule → data incomplete.
 *  - Outstanding Commitments are UNPAID purchase installments only — never a
 *    mortgage balance.
 *
 * Reliability rules:
 *  - Schedule present & usable:
 *      outstanding = Σ unpaid installments (reliable).
 *      paid-in = Σ paid installments + acquisition fees (fees counted once any
 *      installment is paid). If the asset is financed we cannot time loan draws
 *      vs investor funds across installments → paid-in marked INCOMPLETE.
 *  - No schedule, OFF-PLAN (property_type 'off_plan' or is_new_build): cannot
 *      split paid vs future → both INCOMPLETE.
 *  - No schedule, completed/resale: no recorded future obligation → outstanding
 *      = 0 (reliable); paid-in = acq − loan_amount (investor equity, reliable).
 */
function computeAssetCapital(a) {
  const price = num(a.purchase_price);
  const acq = num(a.total_investment) || price;
  const financed = !!a.is_financed && num(a.loan_amount) > 0;
  const loan = financed ? num(a.loan_amount) : 0;
  const isOffplan =
    a.property_type === "off_plan" ||
    !!a.is_new_build ||
    (a.registration_mode === "simplified_global" &&
      (a.property_status === "off_plan" || a.property_status === "under_construction"));
  const extraFees = Math.max(acq - price, 0);

  const rawMs = Array.isArray(a.payment_milestones) ? a.payment_milestones : [];
  const amountOf = (m) => {
    if (m.amount !== undefined && m.amount !== null && m.amount !== "") return num(m.amount);
    if (m.percentage !== undefined && m.percentage !== null && m.percentage !== "") {
      return (num(m.percentage) / 100) * price;
    }
    return null;
  };
  const hasPaidFlag = (m) => typeof m.is_paid === "boolean" || typeof m.paid === "boolean";
  const isPaid = (m) => m.is_paid === true || m.paid === true;
  const msUsable = rawMs.length > 0 && rawMs.every((m) => hasPaidFlag(m) && amountOf(m) !== null);

  const INCOMPLETE = { paid: { reliable: false }, outstanding: { reliable: false } };

  if (rawMs.length > 0 && !msUsable) return INCOMPLETE; // ambiguous schedule

  if (msUsable) {
    let paidPurchase = 0, unpaidPurchase = 0;
    for (const m of rawMs) {
      const amt = amountOf(m);
      if (isPaid(m)) paidPurchase += amt; else unpaidPurchase += amt;
    }
    const outstanding = { reliable: true, local: unpaidPurchase };
    const paidInGross = paidPurchase + (paidPurchase > 0 ? extraFees : 0);
    const paid = financed ? { reliable: false } : { reliable: true, local: paidInGross };
    return { paid, outstanding };
  }

  // No schedule
  if (isOffplan) return INCOMPLETE;
  return {
    outstanding: { reliable: true, local: 0 },
    paid: { reliable: true, local: Math.max(acq - loan, 0) },
  };
}

/**
 * Redesigned EQUIA Portfolio dashboard — VISUAL FOUNDATION ONLY.
 *
 * This is a new, additive page mounted at /portfolio. The original dashboard
 * at /dashboard (DashboardPage.js) is untouched and fully restorable. Per the
 * design brief, this step implements layout + styling only: no new financial
 * calculations and no fake values — KPI and allocation figures render as
 * "Not available" / neutral states until a later step wires real data.
 */
export default function PortfolioRedesignPage() {
  const { fmt, fmtSigned, rates } = useCurrency();
  const [assets, setAssets] = useState([]);
  const [fx, setFx] = useState({ available: null, eur: 0, updatedAt: null, stale: false, source: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(false);
      // Read-only. summary -> properties + countries; performance -> the
      // existing FX-impact figure (independent of tax/fee engines); fx/rates ->
      // the last reference-rate update. allSettled so a secondary failure does
      // not break the properties list.
      const [sumRes, perfRes, fxRes] = await Promise.allSettled([
        api.get("/portfolio/summary"),
        api.get("/portfolio/performance?months=12"),
        api.get("/fx/rates?base=EUR"),
      ]);
      if (sumRes.status === "rejected") throw sumRes.reason;
      const sdata = sumRes.value.data || {};
      const list = sdata.assets || [];
      setAssets(list);

      if (perfRes.status === "fulfilled" && fxRes.status === "fulfilled") {
        const p = perfRes.value.data || {};
        const f = fxRes.value.data || {};
        const impact = p.fx_impact_eur;
        setFx({
          available: p.fx_basis_available === true && impact !== null && impact !== undefined,
          eur: Number(impact || 0),
          updatedAt: f.updated_at || null,
          stale: !!f.stale,
          source: f.source || null,
        });
      } else {
        // never invent FX data — mark unavailable
        setFx({ available: false, eur: 0, updatedAt: null, stale: false, source: null });
      }
      return list;
    } catch (e) {
      setError(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Aggregate Paid-In Capital & Outstanding Commitments across the portfolio.
  // Each asset's local-currency figure is converted to EUR via live rates
  // (toEur); the EUR total is rendered through fmt(..,"EUR") so it reacts to
  // the EUR/AED/GBP selector. Only reliable per-asset figures are summed;
  // assets with insufficient data are counted as incomplete, never guessed.
  const capital = useMemo(() => {
    // Convert local -> EUR using live rates. Returns null when a non-EUR amount
    // cannot be converted yet (rates not loaded / missing) so we never render an
    // unconverted figure under the wrong currency label — such assets are
    // counted as incomplete until rates arrive.
    const toEurSafe = (amount, cur) => {
      if (!amount) return 0;
      if (cur === "EUR") return amount;
      const r = rates[cur];
      return r && r > 0 ? amount / r : null;
    };
    let paidEur = 0, outEur = 0;
    let paidReliable = 0, outReliable = 0, paidIncomplete = 0, outIncomplete = 0;
    for (const a of assets) {
      const c = computeAssetCapital(a);
      const own = ownFracOf(a); // investor share; 100% when unset
      if (c.paid.reliable) {
        const v = toEurSafe(c.paid.local, a.currency);
        if (v === null) paidIncomplete += 1; else { paidEur += v * own; paidReliable += 1; }
      } else paidIncomplete += 1;
      if (c.outstanding.reliable) {
        const v = toEurSafe(c.outstanding.local, a.currency);
        if (v === null) outIncomplete += 1; else { outEur += v * own; outReliable += 1; }
      } else outIncomplete += 1;
    }
    return {
      total: assets.length,
      paid: { eur: paidEur, reliableCount: paidReliable, incompleteCount: paidIncomplete },
      outstanding: { eur: outEur, reliableCount: outReliable, incompleteCount: outIncomplete },
    };
  }, [assets, rates]);

  // ESTIMATED MARKET VALUE — only from explicit valuation records; purchase
  // price is NEVER used as a proxy. Reads a manually-entered valuation field if
  // present; absent today -> coveredCount 0 -> card shows "Not available".
  // Each valuation is scaled by ownership_pct (when present) so a part-owned
  // property never overstates the user's share of portfolio value.
  const marketValue = useMemo(() => {
    let eur = 0, covered = 0, unconvertible = 0;
    for (const a of assets) {
      const v = valuationOf(a);
      if (v === null) continue;
      if (a.currency !== "EUR" && !(rates[a.currency] > 0)) { unconvertible += 1; continue; } // valuation exists but FX not available
      const ownPct = num(a.ownership_pct);
      const ownFrac = ownPct > 0 && ownPct <= 100 ? ownPct / 100 : 1; // default to 100% when unset
      eur += (a.currency === "EUR" ? v : v / rates[a.currency]) * ownFrac;
      covered += 1;
    }
    return { eur, coveredCount: covered, unconvertibleCount: unconvertible };
  }, [assets, rates]);

  // Capital Allocation by Country — groups RELIABLE paid-in capital (investor
  // funds, incl. acquisition fees, excl. loan) by each asset's country, in EUR.
  // Purchase prices are NOT used. Countries are derived from the data (any
  // number; nothing hardcoded). Included assets sum to 100%.
  const allocation = useMemo(() => {
    const toEurSafe = (amount, cur) => {
      if (!amount) return 0;
      if (cur === "EUR") return amount;
      const r = rates[cur];
      return r && r > 0 ? amount / r : null;
    };
    const byCountry = {};
    let total = 0, included = 0, incomplete = 0;
    for (const a of assets) {
      const c = computeAssetCapital(a);
      if (!c.paid.reliable) { incomplete += 1; continue; }
      const eur = toEurSafe(c.paid.local, a.currency);
      if (eur === null) { incomplete += 1; continue; }
      const eurShare = eur * ownFracOf(a); // investor's share of paid-in capital
      const key = a.location === "other" ? (a.country_name || "Other") : (a.location || "unknown");
      byCountry[key] = (byCountry[key] || 0) + eurShare;
      total += eurShare;
      included += 1;
    }
    const rows = Object.entries(byCountry)
      .map(([country, eur]) => ({ country, eur, pct: total > 0 ? (eur / total) * 100 : 0 }))
      .sort((x, y) => y.eur - x.eur);
    return { rows, totalEur: total, includedCount: included, incompleteCount: incomplete, total: assets.length };
  }, [assets, rates]);

  // Build the display model for a KPI card driven by real data.
  const computedCard = (m) => {
    if (capital.total === 0) return { kind: "na" };
    if (m.reliableCount === 0) return { kind: "incomplete", incompleteCount: m.incompleteCount, total: capital.total };
    return {
      kind: "value",
      text: fmt(m.eur, "EUR"),
      incompleteCount: m.incompleteCount,
      total: capital.total,
    };
  };

  const mvCard = capital.total === 0
    ? { kind: "na" }
    : marketValue.coveredCount === 0
      ? { kind: "na", footnote: marketValue.unconvertibleCount > 0
            ? `${marketValue.unconvertibleCount} valuation${marketValue.unconvertibleCount > 1 ? "s" : ""} recorded, but in a currency we can't convert to EUR yet`
            : "No valuations recorded yet" }
      : {
          kind: "value",
          text: fmt(marketValue.eur, "EUR"),
          footnote: marketValue.coveredCount < capital.total
            ? `Covers ${marketValue.coveredCount} of ${capital.total} properties${marketValue.unconvertibleCount > 0 ? ` · ${marketValue.unconvertibleCount} in an unconvertible currency` : ""}`
            : null,
        };

  const fxTooltip = "Effect of exchange-rate movements on your committed capital since each property's purchase date. Invested amounts are held constant in their local currency and revalued at today's reference rates versus the rate on the purchase date — this isolates currency movement from changes in property market value.";
  const fxCard = capital.total === 0
    ? { kind: "na", tooltip: fxTooltip }
    : fx.available !== true
      ? { kind: "na", tooltip: fxTooltip, footnote: "Historical FX rates unavailable for one or more purchase dates" }
      : {
          kind: "value",
          text: fmtSigned(fx.eur, "EUR"),
          color: fx.eur > 0.5 ? "green" : fx.eur < -0.5 ? "red" : "default",
          tooltip: fxTooltip,
          footnote: fx.updatedAt
            ? `FX updated ${new Date(fx.updatedAt).toLocaleString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} UTC · ${fx.source || ""}${fx.stale ? " · stale" : ""}`
            : null,
        };

  const KPIS = [
    { id: "paid-in-capital", label: "Paid-In Capital", hint: "Investor capital actually contributed to date", card: computedCard(capital.paid) },
    { id: "outstanding-commitments", label: "Outstanding Commitments", hint: "Remaining unpaid purchase installments (excl. mortgage)", card: computedCard(capital.outstanding) },
    { id: "estimated-market-value", label: "Estimated Market Value", hint: "Based on recorded valuations only — never purchase price", card: mvCard },
    { id: "currency-impact", label: "Currency Impact", hint: "FX movement on committed capital since acquisition", card: fxCard },
  ];

  return (
    <div data-testid="portfolio-redesign-page" className="max-w-[1400px]">
      {/* ── Main header ─────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between mb-10 md:mb-14">
        <div>
          <p
            data-testid="portfolio-eyebrow"
            className="text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase font-sans mb-3"
          >
            EQUIA / Portfolio
          </p>
          <h1
            data-testid="portfolio-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]"
          >
            Your Global Portfolio
          </h1>
          <p
            data-testid="portfolio-subtitle"
            className="text-white/55 text-sm sm:text-base mt-4 font-sans max-w-xl leading-relaxed"
          >
            Your international real estate investments at a glance.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => setShowArchive(true)}
            data-testid="open-archive-btn"
            aria-label="View archived properties"
            title="View archived properties"
            className="border border-white/15 text-white/85 px-4 py-2.5 text-sm font-sans hover:border-white/30 hover:text-white transition-colors flex items-center gap-2 min-h-[44px]"
          >
            <Archive className="w-4 h-4" /> <span className="hidden sm:inline">Archive</span>
          </button>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-asset-btn"
            className="bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-medium font-sans hover:bg-[#B5952F] transition-colors flex items-center justify-center gap-2 min-h-[44px]"
          >
            <Plus className="w-4 h-4" /> Add Asset
          </button>
        </div>
      </div>

      {/* ── SECTION 1 — Financial KPIs (2×2) ────────────────────────── */}
      <section className="mb-10 md:mb-12" data-testid="kpi-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {KPIS.map((kpi, i) => (
            <motion.div
              key={kpi.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
              data-testid={`kpi-${kpi.id}`}
              className="bg-[#0A0A0A] border border-white/[0.10] p-6 md:p-7 min-h-[150px] flex flex-col justify-between hover:border-white/[0.18] transition-colors"
            >
              <p className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-white/55 font-sans">
                {kpi.label}
                {kpi.card.tooltip && (
                  <span
                    className="group relative inline-flex items-center justify-center w-3.5 h-3.5 border border-white/15 rounded-full text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 cursor-help transition-colors"
                    data-testid={`kpi-${kpi.id}-info`}
                  >
                    <Info className="w-2 h-2" strokeWidth={2} />
                    <span className="invisible group-hover:visible absolute left-0 top-5 z-20 w-72 max-w-[calc(100vw-2rem)] bg-[#0A0A0A] border border-white/[0.1] text-white/90 text-[11px] leading-relaxed p-3 shadow-2xl normal-case tracking-normal">
                      {kpi.card.tooltip}
                    </span>
                  </span>
                )}
              </p>
              <div>
                {loading ? (
                  <div className="h-7 w-32 bg-white/[0.06] animate-pulse rounded-sm" />
                ) : kpi.card.kind === "value" ? (
                  <>
                    <p
                      className={`font-mono text-2xl md:text-3xl leading-none ${
                        kpi.card.color === "green" ? "text-emerald-400"
                        : kpi.card.color === "red" ? "text-red-400"
                        : "text-white"
                      }`}
                      data-testid={`kpi-${kpi.id}-value`}
                    >
                      {kpi.card.text}
                    </p>
                    {kpi.card.incompleteCount > 0 && (
                      <p className="flex items-center gap-1.5 text-amber-400/80 text-[10px] mt-2 font-sans" data-testid={`kpi-${kpi.id}-incomplete`}>
                        <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                        {kpi.card.incompleteCount} of {kpi.card.total} properties have incomplete data
                      </p>
                    )}
                  </>
                ) : kpi.card.kind === "incomplete" ? (
                  <p className="flex items-center gap-1.5 font-mono text-lg md:text-xl text-amber-400/80 leading-none" data-testid={`kpi-${kpi.id}-value`}>
                    <AlertTriangle className="w-4 h-4" strokeWidth={2} /> Incomplete data
                  </p>
                ) : (
                  <p className="font-mono text-2xl md:text-3xl text-white/35 leading-none" data-testid={`kpi-${kpi.id}-value`}>
                    {NA}
                  </p>
                )}
                <p className="text-white/35 text-[11px] mt-3 font-sans leading-relaxed" data-testid={`kpi-${kpi.id}-footnote`}>
                  {kpi.card.kind === "incomplete"
                    ? `${kpi.card.incompleteCount} of ${kpi.card.total} properties need payment/loan details`
                    : (kpi.card.footnote || kpi.hint)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── SECTION 2 — Capital Allocation by Country ───────────────── */}
      <section className="mb-10 md:mb-12" data-testid="allocation-section">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="capital-allocation-card"
          className="bg-[#0A0A0A] border border-white/[0.10] p-6 md:p-8"
        >
          <div className="mb-6">
            <h2 className="font-serif text-xl md:text-2xl text-white tracking-tight">
              Capital Allocation by Country
            </h2>
            <p className="text-white/50 text-[13px] mt-1.5 font-sans">
              Distribution of your paid-in capital across countries.
            </p>
          </div>

          {loading ? (
            <div className="space-y-5">
              <div className="h-3.5 w-full bg-white/[0.05] animate-pulse rounded-full" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[0, 1].map((n) => (
                  <div key={n} className="h-4 w-full bg-white/[0.04] animate-pulse rounded-sm" />
                ))}
              </div>
            </div>
          ) : allocation.rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="allocation-empty">
              <div className="w-full h-px border-t border-dashed border-white/[0.12] mb-8" />
              <MapPin className="w-9 h-9 text-white/[0.08] mb-3" strokeWidth={1} />
              <p className="text-white/50 text-sm font-sans">No paid-in capital to allocate yet</p>
              <p className="text-white/30 text-[11px] mt-1 font-sans">
                {allocation.incompleteCount > 0
                  ? `${allocation.incompleteCount} of ${allocation.total} properties have incomplete payment data`
                  : "Add a property to see its country allocation"}
              </p>
            </div>
          ) : (
            <div data-testid="allocation-content">
              {/* One elegant horizontal stacked bar */}
              <div
                className="flex w-full h-3.5 rounded-full overflow-hidden bg-white/[0.04] gap-[2px]"
                data-testid="allocation-bar"
              >
                {allocation.rows.map((r, i) => (
                  <div
                    key={r.country}
                    data-testid={`allocation-seg-${r.country}`}
                    title={`${humanize(r.country)} · ${r.pct.toFixed(1)}%`}
                    className="h-full first:rounded-l-full last:rounded-r-full transition-[width] duration-500"
                    style={{ width: `${Math.max(r.pct, 1.5)}%`, backgroundColor: ALLOC_COLORS[i % ALLOC_COLORS.length] }}
                  />
                ))}
              </div>

              {/* Compact legend */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="allocation-legend">
                {allocation.rows.map((r, i) => (
                  <div key={r.country} className="flex items-center justify-between gap-4" data-testid={`allocation-legend-${r.country}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: ALLOC_COLORS[i % ALLOC_COLORS.length] }} />
                      <span className="text-white/80 text-sm font-sans truncate">{humanize(r.country)}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-xs text-white/50 w-12 text-right">{r.pct.toFixed(1)}%</span>
                      <span className="font-mono text-xs text-white w-28 text-right whitespace-nowrap" data-testid={`allocation-amount-${r.country}`}>
                        {fmt(r.eur, "EUR")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclosure of included capital + total */}
              <div className="mt-6 pt-4 border-t border-white/[0.08] flex items-center justify-between gap-4">
                <p className="text-white/40 text-[11px] font-sans" data-testid="allocation-disclosure">
                  {allocation.incompleteCount > 0
                    ? `Based on ${allocation.includedCount} of ${allocation.total} properties with reliable paid-in data · ${allocation.incompleteCount} excluded`
                    : `Based on all ${allocation.includedCount} ${allocation.includedCount === 1 ? "property" : "properties"}`}
                </p>
                <p className="font-mono text-xs text-white/70 shrink-0 whitespace-nowrap" data-testid="allocation-total">
                  {fmt(allocation.totalEur, "EUR")}
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* ── SECTION 3 — Your Properties ─────────────────────────────── */}
      <section className="mb-4" data-testid="properties-section">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          data-testid="your-properties-card"
          className="bg-[#0A0A0A] border border-white/[0.10] p-6 md:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl md:text-2xl text-white tracking-tight">Your Properties</h2>
            {!loading && !error && (
              <span className="text-[10px] tracking-[0.15em] uppercase text-white/40 font-sans">
                {assets.length} {assets.length === 1 ? "property" : "properties"}
              </span>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((n) => (
                <div key={n} className="h-16 w-full bg-white/[0.04] animate-pulse rounded-sm" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <p className="text-white/50 text-sm font-sans">{NA}</p>
              <button
                onClick={fetchData}
                data-testid="properties-retry-btn"
                className="mt-3 text-[#D4AF37] text-xs hover:underline font-sans"
              >
                Try again
              </button>
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-full h-px border-t border-dashed border-white/[0.12] mb-8" />
              <Building className="w-10 h-10 text-white/[0.08] mb-3" strokeWidth={1} />
              <p className="text-white/55 text-sm font-sans">No properties yet</p>
              <p className="text-white/30 text-[11px] mt-1 mb-5 font-sans">Add your first property to start building your global portfolio</p>
              <button
                onClick={() => setShowAdd(true)}
                data-testid="properties-empty-add-btn"
                className="bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-medium font-sans hover:bg-[#B5952F] transition-colors flex items-center gap-2 min-h-[44px]"
              >
                <Plus className="w-4 h-4" /> Add Asset
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {assets.map((a) => {
                const Icon = locationIcons[a.location] || Building;
                const cap = computeAssetCapital(a);
                const paid = cap.paid.reliable ? fmt(cap.paid.local, a.currency) : NA;
                const outstanding = cap.outstanding.reliable ? fmt(cap.outstanding.local, a.currency) : NA;
                const val = valuationOf(a);
                const market = val !== null ? fmt(val, a.currency) : NA;
                const locality = a.location === "other"
                  ? [a.country_name || "Global", a.city].filter(Boolean).join(" · ")
                  : [locationLabels[a.location] || humanize(a.location), a.location === "germany" ? a.bundesland : null].filter(Boolean).join(" · ");
                return (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAsset(a)}
                    data-testid={`property-row-${a.id}`}
                    className="w-full group text-left border border-white/[0.08] hover:border-[#D4AF37]/30 bg-white/[0.01] hover:bg-white/[0.03] p-4 md:p-5 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <span className="w-10 h-10 shrink-0 flex items-center justify-center border border-white/10 text-white/60 group-hover:text-[#D4AF37] group-hover:border-[#D4AF37]/40 transition-colors">
                          <Icon className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-sans truncate">{a.name || "Unnamed property"}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-white/45 text-[11px] font-sans truncate">{locality}</span>
                            <span className="text-[9px] tracking-[0.1em] uppercase text-[#D4AF37]/80 border border-[#D4AF37]/25 px-1.5 py-0.5 font-sans whitespace-nowrap" data-testid={`property-status-${a.id}`}>
                              {statusOf(a)}
                            </span>
                            {num(a.ownership_pct) > 0 && num(a.ownership_pct) < 100 && (
                              <span className="text-[9px] tracking-[0.1em] uppercase text-white/55 border border-white/15 px-1.5 py-0.5 font-sans whitespace-nowrap" data-testid={`property-ownership-${a.id}`}>
                                {num(a.ownership_pct)}% owned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-white/25 group-hover:text-white/60 transition-colors shrink-0 mt-1" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-4 pt-4 border-t border-white/[0.06] pl-0 md:pl-14">
                      <PropStat label="Purchase Price" value={fmt(a.purchase_price, a.currency)} testid={`property-price-${a.id}`} />
                      <PropStat label="Paid-In Capital" value={paid} testid={`property-paidin-${a.id}`} />
                      <PropStat label="Outstanding" value={outstanding} testid={`property-outstanding-${a.id}`} />
                      <PropStat label="Market Value" value={market} testid={`property-market-${a.id}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>
      </section>

      <AddAssetDialog open={showAdd} onClose={() => setShowAdd(false)} onSuccess={fetchData} enableCountryPicker />
      <ArchiveModal open={showArchive} onClose={() => setShowArchive(false)} onAnyChange={fetchData} />
      <AssetDetailModal
        open={!!selectedAsset}
        asset={selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onRefresh={async () => {
          const fresh = await fetchData();
          setSelectedAsset((curr) => (curr ? fresh.find((x) => x.id === curr.id) || null : null));
        }}
      />
    </div>
  );
}
